import React, { useState, useEffect } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { aboutPageApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import RichTextEditor from '../../components/admin/RichTextEditor';
import ImageUpload from '../../components/admin/ImageUpload';

const AdminAbout = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await aboutPageApi.get();
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching about page settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await aboutPageApi.update(settings);
      setMessage('Settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Error saving settings');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div data-testid="admin-about-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">About Page Settings</h1>
        <div className="flex items-center gap-4">
          {message && (
            <span className={`text-sm ${message.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
              {message}
            </span>
          )}
          <Button onClick={handleSave} disabled={saving} data-testid="save-about-btn">
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
            ) : (
              <><Save className="h-4 w-4 mr-2" />Save Changes</>
            )}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="hero" className="space-y-6">
        <TabsList>
          <TabsTrigger value="hero">Hero Section</TabsTrigger>
          <TabsTrigger value="main">Main Content</TabsTrigger>
          <TabsTrigger value="services">Services</TabsTrigger>
          <TabsTrigger value="stats">Statistics</TabsTrigger>
        </TabsList>

        {/* Hero Section */}
        <TabsContent value="hero">
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
            <h2 className="text-lg font-medium text-gray-900 border-b pb-2">Hero Section</h2>
            <div className="grid grid-cols-1 gap-6">
              <div className="space-y-2">
                <Label htmlFor="heroTitle">Hero Title</Label>
                <Input
                  id="heroTitle"
                  value={settings?.heroTitle || ''}
                  onChange={(e) => handleChange('heroTitle', e.target.value)}
                  placeholder="About Us"
                  data-testid="hero-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="heroSubtitle">Hero Subtitle</Label>
                <Input
                  id="heroSubtitle"
                  value={settings?.heroSubtitle || ''}
                  onChange={(e) => handleChange('heroSubtitle', e.target.value)}
                  placeholder="A brief tagline for the about page"
                  data-testid="hero-subtitle-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="heroImage">Hero Image URL</Label>
                <Input
                  id="heroImage"
                  value={settings?.heroImage || ''}
                  onChange={(e) => handleChange('heroImage', e.target.value)}
                  placeholder="https://images.unsplash.com/..."
                  data-testid="hero-image-input"
                />
                {settings?.heroImage && (
                  <img 
                    src={settings.heroImage} 
                    alt="Hero preview" 
                    className="mt-2 h-32 w-full object-cover rounded-lg"
                  />
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Main Content */}
        <TabsContent value="main">
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
            <h2 className="text-lg font-medium text-gray-900 border-b pb-2">Main Content</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="mainHeading">Main Heading</Label>
                <Input
                  id="mainHeading"
                  value={settings?.mainHeading || ''}
                  onChange={(e) => handleChange('mainHeading', e.target.value)}
                  placeholder="Bringing World-Class Lighting"
                  data-testid="main-heading-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mainHeadingHighlight">Heading Highlight</Label>
                <Input
                  id="mainHeadingHighlight"
                  value={settings?.mainHeadingHighlight || ''}
                  onChange={(e) => handleChange('mainHeadingHighlight', e.target.value)}
                  placeholder="to the UK Market"
                  data-testid="main-heading-highlight-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mainContent">Main Paragraph</Label>
              <RichTextEditor
                id="mainContent"
                rows={4}
                value={settings?.mainContent || ''}
                onChange={(value) => handleChange('mainContent', value)}
                placeholder="The main description paragraph..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="secondaryContent">Secondary Paragraph</Label>
              <RichTextEditor
                id="secondaryContent"
                rows={4}
                value={settings?.secondaryContent || ''}
                onChange={(value) => handleChange('secondaryContent', value)}
                placeholder="Additional description..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tertiaryContent">Tertiary Paragraph</Label>
              <RichTextEditor
                id="tertiaryContent"
                rows={4}
                value={settings?.tertiaryContent || ''}
                onChange={(value) => handleChange('tertiaryContent', value)}
                placeholder="More description..."
              />
            </div>
          </div>
        </TabsContent>

        {/* Services Section */}
        <TabsContent value="services">
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
            <h2 className="text-lg font-medium text-gray-900 border-b pb-2">Services Section</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="servicesTitle">Section Title</Label>
                <Input
                  id="servicesTitle"
                  value={settings?.servicesTitle || ''}
                  onChange={(e) => handleChange('servicesTitle', e.target.value)}
                  placeholder="What We Do"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="servicesSubtitle">Section Subtitle</Label>
                <Input
                  id="servicesSubtitle"
                  value={settings?.servicesSubtitle || ''}
                  onChange={(e) => handleChange('servicesSubtitle', e.target.value)}
                  placeholder="We provide comprehensive support..."
                />
              </div>
            </div>

            {/* Service 1 */}
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-medium text-gray-700">Service 1</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="service1Title">Title</Label>
                  <Input
                    id="service1Title"
                    value={settings?.service1Title || ''}
                    onChange={(e) => handleChange('service1Title', e.target.value)}
                    placeholder="Product Specification"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service1Description">Description</Label>
                  <RichTextEditor
                    id="service1Description"
                    rows={2}
                    value={settings?.service1Description || ''}
                    onChange={(value) => handleChange('service1Description', value)}
                    placeholder="Expert guidance on selecting..."
                  />
                </div>
              </div>
            </div>

            {/* Service 2 */}
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-medium text-gray-700">Service 2</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="service2Title">Title</Label>
                  <Input
                    id="service2Title"
                    value={settings?.service2Title || ''}
                    onChange={(e) => handleChange('service2Title', e.target.value)}
                    placeholder="Technical Support"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service2Description">Description</Label>
                  <RichTextEditor
                    id="service2Description"
                    rows={2}
                    value={settings?.service2Description || ''}
                    onChange={(value) => handleChange('service2Description', value)}
                    placeholder="In-depth technical assistance..."
                  />
                </div>
              </div>
            </div>

            {/* Service 3 */}
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-medium text-gray-700">Service 3</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="service3Title">Title</Label>
                  <Input
                    id="service3Title"
                    value={settings?.service3Title || ''}
                    onChange={(e) => handleChange('service3Title', e.target.value)}
                    placeholder="Project Coordination"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service3Description">Description</Label>
                  <RichTextEditor
                    id="service3Description"
                    rows={2}
                    value={settings?.service3Description || ''}
                    onChange={(value) => handleChange('service3Description', value)}
                    placeholder="Seamless coordination..."
                  />
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Statistics */}
        <TabsContent value="stats">
          <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
            <h2 className="text-lg font-medium text-gray-900 border-b pb-2">Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Stat 1 */}
              <div className="border rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-gray-700">Statistic 1</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="stat1Value">Value</Label>
                    <Input
                      id="stat1Value"
                      value={settings?.stat1Value || ''}
                      onChange={(e) => handleChange('stat1Value', e.target.value)}
                      placeholder="6+"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stat1Label">Label</Label>
                    <Input
                      id="stat1Label"
                      value={settings?.stat1Label || ''}
                      onChange={(e) => handleChange('stat1Label', e.target.value)}
                      placeholder="Partner Brands"
                    />
                  </div>
                </div>
              </div>

              {/* Stat 2 */}
              <div className="border rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-gray-700">Statistic 2</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="stat2Value">Value</Label>
                    <Input
                      id="stat2Value"
                      value={settings?.stat2Value || ''}
                      onChange={(e) => handleChange('stat2Value', e.target.value)}
                      placeholder="15+"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stat2Label">Label</Label>
                    <Input
                      id="stat2Label"
                      value={settings?.stat2Label || ''}
                      onChange={(e) => handleChange('stat2Label', e.target.value)}
                      placeholder="Countries Served"
                    />
                  </div>
                </div>
              </div>

              {/* Stat 3 */}
              <div className="border rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-gray-700">Statistic 3</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="stat3Value">Value</Label>
                    <Input
                      id="stat3Value"
                      value={settings?.stat3Value || ''}
                      onChange={(e) => handleChange('stat3Value', e.target.value)}
                      placeholder="20+"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stat3Label">Label</Label>
                    <Input
                      id="stat3Label"
                      value={settings?.stat3Label || ''}
                      onChange={(e) => handleChange('stat3Label', e.target.value)}
                      placeholder="Years Experience"
                    />
                  </div>
                </div>
              </div>

              {/* Stat 4 */}
              <div className="border rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-gray-700">Statistic 4</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="stat4Value">Value</Label>
                    <Input
                      id="stat4Value"
                      value={settings?.stat4Value || ''}
                      onChange={(e) => handleChange('stat4Value', e.target.value)}
                      placeholder="100+"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stat4Label">Label</Label>
                    <Input
                      id="stat4Label"
                      value={settings?.stat4Label || ''}
                      onChange={(e) => handleChange('stat4Label', e.target.value)}
                      placeholder="Design Partners"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminAbout;
